import React from "react";
import {useLocalStore} from "mobx-react";
import {action, computed, observable, observe, runInAction} from "mobx";
import {ChartStore} from "./chartStore";
import fromPairs from "lodash.frompairs";
import * as csvParse from 'csv-parse';

export interface WemChartStrategy {
  url: string
  name: string
}

export interface WemChartSettings {
  strategies: WemChartStrategy[]
  csv?: csvParse.Options
  dateFormat?: string
}

export const NO_STRATEGY_KEY = 'NONE'
export const DEFAULT_STRATEGY_KEY = '0'
export const PLUGIN_BASE_URL = `${process.env.PUBLIC_URL}/wp-json/wem-chart/v1`
export const SETTINGS_URL = `/wp-json/wem-chart/v1/settings`

export class RootStore {
  @observable settingsLoading = false
  @observable selectedStrategyKey = NO_STRATEGY_KEY
  @observable settingsLoadingError: string | undefined
  @observable settings?: WemChartSettings

  readonly init = () => {
    observe(this, 'chartStores', (e) => runInAction(() => {
      this.selectedStrategyKey = e.newValue ? DEFAULT_STRATEGY_KEY : NO_STRATEGY_KEY
    }))
    this.loadSettings()
  }

  @computed get selectedChartStore(): ChartStore | undefined {
    return this.chartStores[this.selectedStrategyKey]
  }

  @computed get chartStores(): Record<string, ChartStore> {
    const {strategies = []} = this
    return fromPairs(strategies.map((dataUrl, index) =>
      [index.toString(), new ChartStore(this, dataUrl)]
    ))
  }

  @computed get strategies() {
    return this.settings?.strategies
  }

  @action readonly loadSettings = () => {
    this.settingsLoading = true
    if (process.env.NODE_ENV === 'production') {
      fetch(SETTINGS_URL)
        .then(response => {
          if (response.ok) {
            response.json().then((settings: WemChartSettings) => {
              this.settingsLoading = false
              this.settings = settings
            })
          } else {
            this.settingsLoading = false
            this.settingsLoadingError = `Failed to load settings at: ${response.status} ${response.statusText}`
          }
        })
        .catch(e => {
          this.settingsLoading = false
          this.settingsLoadingError = e.message
        })
    } else {
      this.settings = {
        strategies: [{
          url: `${process.env.PUBLIC_URL}/examples/WEM EQ.csv`,
          name: 'WEM EQ',
        }, {
          url: `${process.env.PUBLIC_URL}/examples/SMART AI TRADE.csv`,
          name: 'SMART AI TRADE',
        }, {
          url: `${process.env.PUBLIC_URL}/examples/YOUR MOM TRADE.csv`,
          name: 'YOUR MOM TRADE',
        }, {
          url: `${process.env.PUBLIC_URL}/examples/YOUR MOM TRADE BROKEN.csv`,
          name: 'BROKEN',
        }]
      }
      this.settingsLoading = false
    }
  }

  @action readonly setSelectedChartKey = (key: string) => {
    this.selectedStrategyKey = key
  }
}

const storeContext = React.createContext<RootStore | null>(null)

export const RootStoreProvider = ({children}: React.PropsWithChildren<{}>) => {
  const store = useLocalStore(() => new RootStore())
  return <storeContext.Provider value={store}>{children}</storeContext.Provider>
}

export const useRootStore = () => {
  const store = React.useContext(storeContext)
  if (!store) {
    // this is especially useful in TypeScript so you don't need to be checking for null all the time
    throw new Error('useRootStore must be used within a RootStoreProvider.')
  }
  return store
}
